/**
 * Scope, tests & wizard — per SCOPE-TESTS-WIZARD-SPEC:
 * the scope screen shows what the project does (details/info per item) and
 * rolls items into Done / Issues / Not done yet with counts; tapping a test
 * (or Start testing on a group) opens the WIZARD as a modal overlay that
 * walks ONE STEP at a time (Previous/Next + progress), ending in the pass_if
 * reveal with Pass/Fail — Fail takes a note and can raise a support request
 * linked by scope_key. Results save with step_index so a half-walked test
 * resumes where it stopped; passed tests drop off behind Show approved with
 * Reopen; ⚠ marks tests with open issues; staff see DEV items and the
 * per-test comment thread.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../App.jsx';
import AIAction from '../components/AIAction.jsx';

/* ZAR helpers for the project-worth board. */
const rand = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA');
const randTotal = (w, key) => rand((w?.surfaces || []).reduce((s, x) => s + Number(x[key] || 0), 0));

/* A notes thread for ANY level — the whole project (testKey sentinel), a test
   detail (stepIndex -1), or a single step (stepIndex >= 0). Text, 🎙 voice
   (transcribed server-side) or ✨ AI draft, all editable before Add. */
function NoteThread({ testKey, stepIndex = -1, label = '', context = '', placeholder = 'Add a note…' }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [src, setSrc] = useState('text');
  const [recOn, setRecOn] = useState(false);
  const recRef = useRef(null);
  const load = useCallback(() => {
    api(`/scope/comments/?test_key=${encodeURIComponent(testKey)}&step_index=${stepIndex}`)
      .then(setNotes).catch(() => {});
  }, [testKey, stepIndex]);
  useEffect(() => { load(); }, [load]);
  const voice = async () => {
    if (recOn) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecOn(false);
        const fd = new FormData();
        fd.append('audio', new Blob(chunks, { type: 'audio/webm' }), 'note.webm');
        try { const d = await api('/scope/comments/assist/', { method: 'POST', form: fd });
              if (d.text) { setText(d.text); setSrc('voice'); } } catch { /* typed works */ }
      };
      recRef.current = rec; rec.start(); setRecOn(true);
    } catch { /* mic refused — typing it is */ }
  };
  const aiBefore = useRef({ text: '', src: 'text' });   // pre-AI snapshot for Undo
  const draftNote = async () => {
    const d = await api('/scope/comments/assist/',
      { method: 'POST', body: { title: label, context } });
    return d.text || null;
  };
  const add = async () => {
    if (!text.trim()) return;
    await api('/scope/comments/', { method: 'POST',
      body: { test_key: testKey, label, body: text, step_index: stepIndex, source: src } });
    setText(''); setSrc('text'); load();
  };
  return (
    <div className="mt-1.5">
      {notes.map((c, k) => (
        <p key={k} className="text-xs text-slate-400 py-0.5">
          <span className="text-slate-300">{c.author || 'note'}:</span> {c.body}
          {c.source && c.source !== 'text' && (
            <span className="text-slate-600"> · {c.source === 'voice' ? '🎙' : '✨'}</span>
          )}
        </p>
      ))}
      <div className="flex gap-2 mt-1.5">
        <input className="input !py-1 text-xs flex-1" placeholder={placeholder}
               value={text} onChange={(e) => { setText(e.target.value); setSrc('text'); }} />
        <button className={`text-xs border rounded-lg px-2 ${recOn ? 'border-red-400 text-red-400 animate-pulse' : 'border-edge text-slate-400'}`}
                title={recOn ? 'Stop and transcribe' : 'Speak the note'} onClick={voice}>🎙</button>
        <AIAction size="sm" label="" title="✨ AI drafts a note"
                  run={() => { aiBefore.current = { text, src }; return draftNote(); }}
                  onResult={(t) => { setText(t); setSrc('ai'); }}
                  onUndo={() => { setText(aiBefore.current.text); setSrc(aiBefore.current.src); }}
                  readback="Drafted — edit before you add it." />
        <button className="text-xs border border-edge rounded-lg px-2 text-slate-400"
                disabled={!text.trim()} onClick={add}>Add</button>
      </div>
    </div>
  );
}

/* ── the wizard OVERLAY: one step per screen ─────────────────────────────── */
function WizardOverlay({ test, startAt, staff, onSaved, onClose,
                        pos = 1, total = 1, hasPrev = false, hasNext = false,
                        onPrev = () => {}, onNext = () => {} }) {
  const [i, setI] = useState(Math.min(startAt || 0, test.steps.length - 1));
  const [finishing, setFinishing] = useState(false);
  const [failing, setFailing] = useState(false);
  const [note, setNote] = useState('');
  const [steps, setSteps] = useState({});   // step_index -> 'to_do' | 'done'
  const [liveSteps, setLiveSteps] = useState(null);  // §3 ✨ AI re-drafted steps
  const sSteps = liveSteps || test.steps;
  useEffect(() => {
    if (staff) {
      api(`/scope/step-state/?test_key=${encodeURIComponent(test.key)}`)
        .then((d) => setSteps(Object.fromEntries((d || []).map((x) => [x.step_index, x.state]))))
        .catch(() => {});
    }
  }, [test.key, staff]);
  const setStep = (idx, state) => {
    setSteps((prev) => ({ ...prev, [idx]: state }));
    api('/scope/step-state/', { method: 'POST',
      body: { test_key: test.key, step_index: idx, state } }).catch(() => {});
  };
  const stepsDone = Object.values(steps).filter((v) => v === 'done').length;
  const stepRefs = useRef({});
  useEffect(() => { stepRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, [i]);

  const saveProgress = (idx) =>
    api('/scope/save/', { method: 'POST',
      body: { test_key: test.key, status: 'pending', label: test.title,
              step_index: idx } }).catch(() => {});

  // §3 ✨ — AI re-drafts detailed, runnable steps for this test. Standardised on
  // the shared AIAction: the steps show at once and Undo restores the originals.
  const redraftSteps = async () => {
    const r = await api('/scope/redraft-steps/', { method: 'POST', body: { test_key: test.key } });
    if (!r.steps?.length) throw { data: { detail: 'AI had no better steps to offer this time.' } };
    return r.steps;
  };

  const finish = async (status, noteText = '', report = false) => {
    await api('/scope/save/', { method: 'POST',
      body: { test_key: test.key, status, label: test.title,
              note: noteText, step_index: 0 } });
    if (report) {
      await api('/feedback/journeys/', { method: 'POST',
        body: { kind: 'problem', scope_key: test.key,
                title: `Scope fail: ${test.title}`,
                description: noteText || `Failed "${test.title}" — expected: ${test.pass_if}`,
                page_url: (test.urls || [])[0] || '', surface: 'frontend',
                capture: 'screenshot' } }).catch(() => {});
    }
    onSaved();
  };

  const pct = Math.round((stepsDone / Math.max(1, sSteps.length)) * 100);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-3"
         onClick={() => { saveProgress(i); onClose(); }}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-[2px]" />
      <div className="relative bg-panel border border-edge rounded-2xl p-5 sm:p-7 w-[95vw] max-w-[1500px]
                      shadow-2xl max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h2 className="font-bold text-lg leading-tight">{test.title}</h2>
            {test.verified_by && (
              <span className="inline-block mt-1 text-[11px] text-emerald-400
                               border border-emerald-400/40 rounded-full px-2 py-0.5">
                ✓ Verified by {test.verified_by}
              </span>
            )}
            {test.details && <p className="text-xs text-slate-400 mt-1">{test.details}</p>}
            {test.info && <p className="text-[11px] text-slate-600 mt-0.5">{test.info}</p>}
          </div>
          <button className="w-8 h-8 rounded-full border border-edge text-slate-400
                             hover:text-white shrink-0"
                  title="Close — progress is saved"
                  onClick={() => { saveProgress(i); onClose(); }}>✕</button>
        </div>

        <div className="flex items-center gap-3 mt-4">
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {total > 1 ? `Test ${pos}/${total} · ` : ''}Step {i + 1}/{sSteps.length} · {stepsDone} done
          </span>
          <div className="flex-1 h-1.5 rounded bg-black/40">
            <div className="h-1.5 rounded bg-vault-gradient transition-all"
                 style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs text-slate-600">{pct}%{staff && stepsDone > 0 ? ` · ${stepsDone}✓` : ''}</span>
        </div>
        {staff && (
          <div className="mt-2">
            <AIAction size="sm" label="Re-draft detailed steps"
                      title="AI re-drafts detailed, runnable steps (surface · URL/command · button · what to look for)"
                      run={redraftSteps}
                      onResult={(steps) => { setLiveSteps(steps); setI(0); }}
                      onUndo={() => { setLiveSteps(null); setI(0); }}
                      readback={(steps) => `Re-drafted ${steps.length} step${steps.length === 1 ? '' : 's'} — review before testing.`} />
          </div>
        )}

        <div className="mt-5 space-y-2.5">
          {sSteps.map((stepText, idx) => (
            <div key={idx} ref={(el) => { stepRefs.current[idx] = el; }}
                 className={`rounded-xl p-3 border transition-colors ${idx === i ? 'border-vblue bg-vblue/5' : 'border-edge/60'}`}>
              <div className="flex items-start gap-2.5">
                {staff ? (
                  <button onClick={() => setStep(idx, steps[idx] === 'done' ? 'to_do' : 'done')}
                          title={steps[idx] === 'done' ? 'Mark to-do' : 'Mark done'}
                          className={`shrink-0 mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center text-xs ${steps[idx] === 'done' ? 'border-emerald-400 text-emerald-400 bg-emerald-400/10' : 'border-edge text-slate-600'}`}>
                    {steps[idx] === 'done' ? '✓' : ''}
                  </button>
                ) : (
                  <span className="shrink-0 mt-0.5 text-xs text-slate-600">{idx + 1}.</span>
                )}
                <p className="text-sm text-slate-100 flex-1">{stepText}</p>
              </div>
              {staff && (
                <NoteThread testKey={test.key} stepIndex={idx} label={`${test.title} — step ${idx + 1}`} context={stepText} placeholder="Note on this step…" />
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-2 mt-3">
          <button className="flex-1 border border-edge rounded-xl py-2 text-sm text-slate-300 disabled:opacity-40"
                  disabled={i === 0 && !hasPrev}
                  onClick={() => { if (i > 0) setI(i - 1); else onPrev(); }}>‹ Previous step</button>
          <button className="flex-1 border border-edge rounded-xl py-2 text-sm text-slate-300 disabled:opacity-40"
                  disabled={i >= sSteps.length - 1 && !hasNext}
                  onClick={() => { if (i < sSteps.length - 1) { const n = i + 1; setI(n); saveProgress(n); } else onNext(); }}>Next step ›</button>
        </div>

        {(test.urls || []).length > 0 && (
          <p className="mt-3">
            {(test.urls || []).map((u) => (
              <a key={u} href={u} target="_blank" rel="noreferrer"
                 className="text-vblue underline text-sm mr-3">🔗 {u.replace('https://', '')}</a>
            ))}
          </p>
        )}

        <div className="mt-4 border border-vpurple/50 rounded-xl p-4">
          <p className="text-xs text-vpurple uppercase tracking-wide mb-1">Pass if</p>
          <p className="text-sm text-slate-100">{test.pass_if}</p>
        </div>

        {failing ? (
          <div className="mt-4">
            <input className="input !py-2 text-sm" autoFocus
                   placeholder="What went wrong? (goes on the report)"
                   value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="flex gap-2 mt-2">
              <button className="flex-1 bg-red-500/90 hover:bg-red-500 text-white rounded-xl py-2.5 text-sm"
                      onClick={() => finish('fail', note, true)}>Fail + report</button>
              <button className="flex-1 border border-red-400/60 text-red-400 rounded-xl py-2.5 text-sm"
                      onClick={() => finish('fail', note)}>Fail only</button>
              <button className="text-xs text-slate-500" onClick={() => setFailing(false)}>Back</button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2 mt-4">
            <button className="flex-1 bg-emerald-500/90 hover:bg-emerald-500 text-white rounded-xl py-2.5 font-medium"
                    onClick={() => finish('pass')}>✓ Approve</button>
            <button className="flex-1 bg-red-500/90 hover:bg-red-500 text-white rounded-xl py-2.5 font-medium"
                    onClick={() => setFailing(true)}>✗ Decline</button>
          </div>
        )}
        {total > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-edge/60">
            <button className="text-xs text-slate-400 disabled:opacity-30" disabled={!hasPrev}
                    onClick={() => { saveProgress(i); onPrev(); }}>‹ Previous test</button>
            <span className="text-[11px] text-slate-600">Test {pos} of {total}</span>
            <button className="text-xs text-slate-400 disabled:opacity-30" disabled={!hasNext}
                    onClick={() => { saveProgress(i); onNext(); }}>Next test ›</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* A done / to-do board: each delivered or to-do LINE gets a done/to-do toggle
   (saved per tester via step-state) and a mic/text/AI comment thread. Reuses the
   same infra as the wizard's per-step notes, keyed by a sentinel test_key. */
function ScopeLineBoard({ title, lines = [], keyName, defaultState = 'to_do', staff }) {
  const [states, setStates] = useState({});
  useEffect(() => {
    api(`/scope/step-state/?test_key=${encodeURIComponent(keyName)}`)
      .then((d) => setStates(Object.fromEntries((d || []).map((x) => [x.step_index, x.state]))))
      .catch(() => {});
  }, [keyName]);
  const setLine = (idx, state) => {
    setStates((prev) => ({ ...prev, [idx]: state }));
    api('/scope/step-state/', { method: 'POST',
      body: { test_key: keyName, step_index: idx, state } }).catch(() => {});
  };
  if (!lines.length) return null;
  const doneCount = lines.filter((_, i) => (states[i] || defaultState) === 'done').length;
  return (
    <div className="card !p-5 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-semibold text-sm text-vblue flex-1">{title}</h3>
        <span className="text-[11px] text-slate-600">{doneCount}/{lines.length} done</span>
      </div>
      <div className="space-y-2">
        {lines.map((line, idx) => {
          const st = states[idx] || defaultState;
          return (
            <div key={idx} className="border border-edge/60 rounded-xl p-3">
              <div className="flex items-start gap-2.5">
                {staff ? (
                  <button onClick={() => setLine(idx, st === 'done' ? 'to_do' : 'done')}
                          title={st === 'done' ? 'Mark to-do' : 'Mark done'}
                          className={`shrink-0 mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center text-xs ${st === 'done' ? 'border-emerald-400 text-emerald-400 bg-emerald-400/10' : 'border-edge text-slate-600'}`}>
                    {st === 'done' ? '✓' : ''}
                  </button>
                ) : (
                  <span className={`shrink-0 mt-0.5 text-xs ${st === 'done' ? 'text-emerald-400' : 'text-slate-600'}`}>{st === 'done' ? '✓' : '○'}</span>
                )}
                <p className={`text-sm flex-1 ${st === 'done' ? 'text-slate-400' : 'text-slate-100'}`}>{line}</p>
              </div>
              {staff && (
                <NoteThread testKey={keyName} stepIndex={idx} label={String(line).slice(0, 60)} context={line} placeholder="Comment on this line…" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── the scope screen ────────────────────────────────────────────────────── */
/* ── §6: the cross-tester MATRIX — every tester's verdict per test, the raw
   truth behind the hub's summary counts. Superuser only. Exported: it also
   serves the rollout's /internal/scope-tests/report/ URL. ────────────────── */
export function Matrix() {
  const [data, setData] = useState(null);
  useEffect(() => { api('/scope/matrix/').then(setData).catch(() => {}); }, []);
  if (!data) return <p className="text-slate-500 text-sm">Loading the matrix…</p>;
  if (!data.testers.length) {
    return <p className="text-slate-500 text-sm">No recorded results yet — run
      tests in the wizard and the matrix fills in.</p>;
  }
  const mark = (s) => (s === 'pass' ? '✓' : s === 'fail' ? '✗' : '·');
  const colour = (s) => (s === 'pass' ? 'text-emerald-400'
    : s === 'fail' ? 'text-red-400' : 'text-slate-600');
  return (
    <div className="overflow-x-auto">
      <table className="text-xs w-full">
        <thead>
          <tr>
            <th className="text-left text-slate-500 font-normal py-1 pr-3 sticky left-0 bg-ink">
              test
            </th>
            {data.testers.map((u) => (
              <th key={u.id} className="text-left text-slate-500 font-normal py-1 px-2
                                        max-w-[120px] truncate">
                {u.email}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.tests.map((t) => (
            <tr key={t.key} className="border-t border-edge/50">
              <td className="py-1 pr-3 sticky left-0 bg-ink max-w-[260px] truncate"
                  title={`${t.group} · ${t.key}`}>
                {t.title}
              </td>
              {data.testers.map((u) => {
                const r = u.results[t.key];
                return (
                  <td key={u.id}
                      className={`py-1 px-2 font-bold ${colour(r?.status)}`}
                      title={r?.note || r?.status || 'pending'}>
                    {mark(r?.status)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── §11 RESULTS REPORT — printable/exportable: every test's PASS/FAIL per
   tester PLUS its comment thread (each attributed to its author), with the
   pass count rolled into project status. Rendered at /internal/scope-tests/report
   and linked from the Tests tab. Superuser only (endpoints gate server-side). ── */
export function ResultsReport() {
  const [matrix, setMatrix] = useState(null);
  const [threads, setThreads] = useState({});
  const [err, setErr] = useState('');
  useEffect(() => {
    api('/scope/matrix/')
      .then(async (mx) => {
        setMatrix(mx);
        const pairs = await Promise.all(
          (mx.tests || []).map((t) =>
            api(`/scope/comments/?test_key=${encodeURIComponent(t.key)}`)
              .then((c) => [t.key, c || []]).catch(() => [t.key, []])));
        setThreads(Object.fromEntries(pairs));
      })
      .catch((e) => setErr(e.data?.detail || e.message));
  }, []);
  if (err) return <p className="text-red-400 text-sm">{err}</p>;
  if (!matrix) return <p className="text-slate-500 text-sm">Compiling the report…</p>;

  const tests = matrix.tests || [];
  const testers = matrix.testers || [];
  const passed = tests.filter((t) =>
    testers.some((u) => u.results?.[t.key]?.status === 'pass')).length;
  const groups = [];
  const gi = {};
  for (const t of tests) {
    const g = t.group || 'Tests';
    if (!(g in gi)) { gi[g] = groups.length; groups.push([g, []]); }
    groups[gi[g]][1].push(t);
  }
  const verdictsFor = (key) => testers
    .map((u) => ({ who: u.email, ...(u.results?.[key] || {}) }))
    .filter((v) => v.status);

  const download = () => {
    const esc = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const body = groups.map(([g, ts]) => {
      const rows = ts.map((t) => {
        const vs = verdictsFor(t.key);
        const vh = vs.length
          ? vs.map((v) => `<div><b>${esc(v.status).toUpperCase()}</b> &mdash; ${esc(v.who)}${v.note ? ': ' + esc(v.note) : ''}</div>`).join('')
          : '<div style="color:#999">no verdict yet</div>';
        const ch = (threads[t.key] || [])
          .map((c) => `<div style="font-size:12px;color:#444">${esc(c.author)}: ${esc(c.body)}</div>`).join('');
        return `<div style="border:1px solid #e3e3e3;border-radius:8px;padding:10px;margin:0 0 8px"><div style="font-weight:600">${esc(t.title)}</div>${vh}${ch ? '<div style="margin-top:6px;border-left:2px solid #eee;padding-left:8px">' + ch + '</div>' : ''}</div>`;
      }).join('');
      return `<h2 style="font-size:13px;text-transform:uppercase;color:#555;border-bottom:1px solid #ddd;padding-bottom:4px;margin:22px 0 8px">${esc(g)}</h2>${rows}`;
    }).join('');
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title>Vault results report</title></head><body style="font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;max-width:900px;margin:0 auto;padding:32px;color:#111"><h1>Vault &mdash; results report</h1><p>${passed} / ${tests.length} tests passed &middot; exported from /scope</p>${body}</body></html>`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([doc], { type: 'text/html' }));
    a.download = 'vault-results-report.html';
    a.click();
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 no-print">
        <p className="text-sm text-slate-400 flex-1">
          <span className="text-emerald-400 font-semibold">{passed}</span> / {tests.length} tests passed ·
          every test's verdict and comment thread, attributed to each tester.
        </p>
        <button className="text-xs border border-edge rounded-lg px-3 py-1.5 text-slate-300"
                onClick={() => window.print()}>Print / PDF</button>
        <button className="btn-primary !px-3 text-xs" onClick={download}>Download HTML</button>
      </div>
      {groups.map(([g, ts]) => (
        <div key={g} className="mb-6">
          <h2 className="text-xs uppercase tracking-wide text-slate-500 mb-2">{g}</h2>
          <div className="card !p-0 divide-y divide-edge">
            {ts.map((t) => {
              const vs = verdictsFor(t.key);
              const cs = threads[t.key] || [];
              return (
                <div key={t.key} className="px-4 py-3">
                  <p className="text-sm font-medium">{t.title}</p>
                  <div className="mt-1 grid gap-0.5">
                    {vs.length === 0 && (
                      <span className="text-xs text-slate-600">No verdict recorded yet.</span>
                    )}
                    {vs.map((v, i) => (
                      <span key={i} className="text-xs">
                        <span className={`font-semibold ${
                          v.status === 'pass' ? 'text-emerald-400'
                          : v.status === 'fail' ? 'text-red-400' : 'text-slate-500'}`}>
                          {String(v.status).toUpperCase()}
                        </span>
                        <span className="text-slate-400"> — {v.who}</span>
                        {v.note && <span className="text-slate-500">: {v.note}</span>}
                      </span>
                    ))}
                  </div>
                  {cs.length > 0 && (
                    <div className="mt-2 ml-1 border-l border-edge pl-3 grid gap-0.5">
                      {cs.map((c, k) => (
                        <p key={k} className="text-xs text-slate-400">
                          <span className="text-slate-300">{c.author}:</span> {c.body}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ScopeWizard() {
  const { me } = useAuth();
  const staff = !!(me?.is_superuser);
  const [cat, setCat] = useState(null);
  const [res, setRes] = useState({});
  const [issues, setIssues] = useState({});
  const [showApproved, setShowApproved] = useState(false);
  const [view, setView] = useState('tests');
  const [runList, setRunList] = useState([]);  // ordered tests in the current run
  const [runIdx, setRunIdx] = useState(-1);    // index into runList; -1 = closed
  const open = runIdx >= 0 && runIdx < runList.length ? runList[runIdx] : null;

  const load = useCallback(() => {
    api('/scope/catalog/').then(setCat).catch(() => {});
    api('/scope/results/').then((rs) => {
      const m = {}; rs.forEach((r) => { m[r.test_key] = r; }); setRes(m);
    }).catch(() => {});
    api('/scope/issues/').then(setIssues).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cat) {
    return <div className="min-h-full grid place-items-center text-slate-500">Loading the scope…</div>;
  }

  const all = cat.groups.flatMap((g) => g.tests);
  const st = (t) => res[t.key]?.status || 'pending';
  const done = all.filter((t) => st(t) === 'pass');
  const broken = all.filter((t) => st(t) === 'fail' || issues[t.key]);
  const todo = all.filter((t) => st(t) === 'pending' && !issues[t.key]);
  const openTest = (t) => {
    const idx = all.findIndex((x) => x.key === t.key);
    setRunList(all); setRunIdx(idx >= 0 ? idx : 0);
  };
  const afterSave = () => {
    load();
    setRunIdx((idx) => (idx + 1 < runList.length ? idx + 1 : -1));
  };
  const gotoTest = (idx) => setRunIdx(() => Math.min(runList.length - 1, Math.max(0, idx)));
  const closeRun = () => { setRunIdx(-1); setRunList([]); load(); };
  // One continuous wizard through EVERY runnable test (approve/decline each,
  // auto-advancing to the next) — not a per-group pick.
  const startAll = () => {
    const runnable = all.filter((t) => showApproved || st(t) !== 'pass' || issues[t.key]);
    if (!runnable.length) return;
    setRunList(runnable);
    setRunIdx(0);
  };
  const startGroup = (g) => {
    const runnable = g.tests.filter((t) => showApproved || st(t) !== 'pass' || issues[t.key]);
    if (!runnable.length) return;
    setRunList(runnable);
    setRunIdx(0);
  };

  const TestChip = ({ t }) => (
    <button className="flex items-center gap-2 w-full text-left border border-edge rounded-xl
                       px-3 py-2 hover:border-vblue transition"
            onClick={() => openTest(t)}>
      <span className="flex-1 text-sm truncate">
        {t.title}
        {t.dev_only && <span className="ml-1.5 text-[10px] text-slate-600">DEV</span>}
        {t.verified_by && st(t) !== 'pass' && (
          <span className="ml-1.5 text-[10px] text-emerald-500">
            ✓ {t.verified_by}
          </span>
        )}
      </span>
      {issues[t.key] ? <span className="text-xs text-amber-400">⚠ {issues[t.key]}</span> : null}
      {st(t) === 'pass' && <span className="text-xs text-emerald-400">Done ✓</span>}
      {st(t) === 'fail' && <span className="text-xs text-red-400">Issue</span>}
      {st(t) === 'pending' && (res[t.key]?.step_index || 0) > 0 && (
        <span className="text-xs text-slate-500">resume step {res[t.key].step_index + 1}</span>
      )}
      {st(t) === 'pass' && (
        <span className="text-[11px] text-slate-600 underline"
              onClick={async (e) => { e.stopPropagation();
                await api('/scope/save/', { method: 'POST',
                  body: { test_key: t.key, status: 'pending', label: t.title } });
                load(); }}>
          Reopen
        </span>
      )}
      {staff && t.source === 'hub-note' && (
        <span className="text-[11px] text-red-500/80 underline"
              onClick={async (e) => { e.stopPropagation();
                if (!window.confirm('Permanently delete this hub note-test? It drops the note on the hub and cannot be undone.')) return;
                await api('/scope/set-hidden/', { method: 'POST',
                  body: { test_key: t.key, permanent: true } });
                load(); }}>
          Delete
        </span>
      )}
    </button>
  );

  return (
    <div className="min-h-full w-full max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <Link to="/app" className="text-slate-500 hover:text-white text-xl">←</Link>
        <h1 className="text-2xl font-bold flex-1">Vault scope</h1>
        <div className="flex gap-1.5">
          {[['scope', 'Scope'], ['tests', 'Tests'],
            ...(staff ? [['matrix', 'Matrix']] : [])].map(([k, label]) => (
            <button key={k}
                    className={`text-xs px-2.5 py-1 rounded-full border ${
                      view === k ? 'border-vblue bg-vblue/15' : 'border-edge text-slate-500'}`}
                    onClick={() => setView(k)}>
              {label}
            </button>
          ))}
        </div>
        <label className="text-xs text-slate-500 flex items-center gap-1.5">
          <input type="checkbox" checked={showApproved}
                 onChange={() => setShowApproved(!showApproved)} />
          Show approved
        </label>
      </div>
      <p className="text-sm text-slate-400 mb-4">
        {done.length} of {all.length} done · {broken.length} issue{broken.length === 1 ? '' : 's'} ·{' '}
        {todo.length} not done yet
      </p>
      {staff && (
        <p className="text-xs mb-4 -mt-2 no-print">
          <Link to="/internal/scope-tests/report" className="text-vblue underline">
            Results report — printable pass/fail + comments ↗
          </Link>
        </p>
      )}

      {view === 'matrix' && staff && <Matrix />}

      {view === 'scope' && (
        <div>
          <div className="card !p-5">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="" className="w-10 h-10" />
              <div className="flex-1">
                <h2 className="font-bold text-lg">{cat.project?.name}
                  <span className="ml-2 text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5
                                   border border-vpurple text-vpurple">{cat.project?.status}</span>
                </h2>
                <p className="text-xs text-slate-500">{cat.project?.domain} · {cat.project?.owner_contact}</p>
              </div>
            </div>
            <p className="text-sm text-slate-300 mt-3">{cat.project?.summary}</p>
          </div>
          {cat.project?.worth && (
            <div className="card !p-5 mt-3">
              <h3 className="font-semibold text-sm text-vblue mb-2">Project worth</h3>
              <p className="text-[11px] text-slate-500 mb-3">{cat.project.worth.basis}</p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="rounded-xl border border-edge p-3">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Replacement cost</p>
                  <p className="text-xl font-bold text-slate-100">{randTotal(cat.project.worth, 'replacement')}</p>
                </div>
                <div className="rounded-xl border border-vpurple/40 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-vpurple">Market value</p>
                  <p className="text-xl font-bold text-slate-100">{randTotal(cat.project.worth, 'market')}</p>
                </div>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 text-left">
                    <th className="font-medium pb-1">Surface</th>
                    <th className="font-medium pb-1 text-right">Replacement</th>
                    <th className="font-medium pb-1 text-right">Market</th>
                  </tr>
                </thead>
                <tbody>
                  {(cat.project.worth.surfaces || []).map((sfc) => (
                    <tr key={sfc.key} className="border-t border-edge/50">
                      <td className="py-1.5 text-slate-300">{sfc.name}</td>
                      <td className="py-1.5 text-right text-slate-400">{rand(sfc.replacement)}</td>
                      <td className="py-1.5 text-right text-slate-400">{rand(sfc.market)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {staff && (
            <div className="card !p-5 mt-3">
              <h3 className="font-semibold text-sm text-vblue mb-2">Project notes</h3>
              <p className="text-[11px] text-slate-500 mb-1">A running diary for the whole project — text, 🎙 voice or ✨ AI.</p>
              <NoteThread testKey="__project__" stepIndex={-1} label={cat.project?.name || 'Project'} context={cat.project?.summary || ''} placeholder="Project note…" />
            </div>
          )}
          <div className="card !p-5 mt-3">
            <h3 className="font-semibold text-sm text-vblue mb-2">What Vault is</h3>
            {(cat.description || []).map((p, i) => (
              <p key={i} className="text-sm text-slate-300 mb-2 leading-relaxed">{p}</p>
            ))}
          </div>
          <ScopeLineBoard title="Delivered — what's done" lines={cat.delivered}
                          keyName="__delivered__" defaultState="done" staff={staff} />
          <ScopeLineBoard title="To do — what's left" lines={cat.todo}
                          keyName="__todo__" defaultState="to_do" staff={staff} />
          <div className="card !p-5 mt-3">
            <h3 className="font-semibold text-sm text-vblue mb-2">Design rules</h3>
            <ul className="text-sm text-slate-300 list-disc ml-5 space-y-1">
              {(cat.design?.rules || []).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
            {cat.design?.tokens?.gradient && (
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-slate-500">Brand gradient:</span>
                {cat.design.tokens.gradient.map((c) => (
                  <span key={c} className="w-5 h-5 rounded" style={{ background: c }} title={c} />
                ))}
                <span className="text-xs text-slate-500 ml-3">Currency: {cat.design.tokens.currency}</span>
              </div>
            )}
          </div>
          {cat.notes_md && (
            <div className="card !p-5 mt-3">
              <h3 className="font-semibold text-sm text-vblue mb-2">NOTES.md — running log</h3>
              <pre className="text-xs text-slate-400 whitespace-pre-wrap font-mono leading-relaxed
                              max-h-96 overflow-y-auto">{cat.notes_md}</pre>
            </div>
          )}
        </div>
      )}

      {view === 'tests' && (
        <div>
          <button className="btn-primary w-full mb-4 flex items-center justify-center gap-2"
                  disabled={!(todo.length + broken.length)}
                  onClick={startAll}>
            ▶ Run all tests as one wizard
            <span className="text-xs opacity-80">({todo.length + broken.length} to go · {done.length}/{all.length} done)</span>
          </button>
          {cat.groups.map((g) => {
            const remaining = g.tests.filter((t) => showApproved || st(t) !== 'pass' || issues[t.key]);
            return (
              <section key={g.group} className="mb-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <h2 className="text-xs uppercase tracking-wide text-vblue flex-1">{g.group}</h2>
                  <span className="text-[11px] text-slate-600">
                    {g.tests.filter((t) => st(t) === 'pass').length}/{g.tests.length} done
                  </span>
                  {remaining.length > 0 && (
                    <button className="text-xs border border-edge rounded-lg px-2 py-0.5
                                       text-slate-400 hover:border-vblue"
                            onClick={() => startGroup(g)}>
                      ▶ Start testing
                    </button>
                  )}
                </div>
                <div className="grid gap-1.5">
                  {(showApproved ? g.tests : remaining).map((t) => <TestChip key={t.key} t={t} />)}
                  {!remaining.length && !showApproved && (
                    <p className="text-xs text-slate-600 px-1">All approved ✓</p>
                  )}
                </div>
              </section>
            );
          })}

          {broken.length > 0 && (
            <section className="mb-5">
              <h2 className="text-xs uppercase tracking-wide text-red-400 mb-1.5">
                Issues — done but broken
              </h2>
              <div className="grid gap-1.5">
                {broken.map((t) => <TestChip key={`i-${t.key}`} t={t} />)}
              </div>
            </section>
          )}
        </div>
      )}

      {open && (
        <WizardOverlay key={open.key} test={open} staff={staff}
                       pos={runIdx + 1} total={runList.length}
                       hasPrev={runIdx > 0} hasNext={runIdx < runList.length - 1}
                       onPrev={() => gotoTest(runIdx - 1)} onNext={() => gotoTest(runIdx + 1)}
                       onSaved={afterSave} onClose={closeRun} />
      )}
    </div>
  );
}
